/* eslint-disable operator-linebreak */

/* eslint-disable import/no-cycle */
import cx from 'classnames';
import noop from 'lodash/noop';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { useTracking } from '../../../Context';
import CdnImage from '../../../components/CdnImage';
import { LayoutContext } from '../../../components/GridLayout/GridLayout';
import RatioContainer from '../../../components/RatioContainer';
import { eventTags } from '../../../core/trackers/events';
import { debugNowTime } from '../../../hooks/useAutoRefresh';
import { useMe } from '../../../profile/hooks';
import Images from '../../../utils/Images';
import { ensureDate } from '../../../utils/date';
import { replaceValues } from '../../../utils/stringUtils';
import FeedbackPopupBlock from '../../../workshops/blocks/FeedbackPopupBlock';
import { OtherCurrentLives } from '../../../workshops/blocks/OtherCurrentLives/OtherCurrentLives';
import WorkshopLiveBlock, { sidebarProps } from '../../../workshops/blocks/WorkshopLiveBlock';
import './CloudTvBlock.scss';
import { useCurrentCloudTvSession, useOtherCurrentLives } from './cloudtv.hooks';
import CloudTvActions from './components/CloudTvActions';
import CloudTvLiveDescription from './components/CloudTvLiveDescription';
import CloudTvRegistration from './components/CloudTvRegistration';

const translationPrefix = 'blocks.cloudtv';

const CloudTvBlock = ({
  _id,
  title,
  container,
  countdown,
  videoProps,
  workshopFilter,
  mode,
  forceLiveStream,
  options,
  liveBlockConfig,
  dependsOnTypology = false,
  ...rest
}) => {
  const {
    showOtherCurrentLives,
    showCurrentLive,
    liveDescriptionConfig = {},
    actions,
    collection,
    showInfoOnlyOnLive = false,
  } = rest;
  const user = useMe();
  const { enableRegistration } = options;
  const { allowFullScreen } = videoProps;
  const { t } = useTranslation();
  const { trackEvent } = useTracking();
  const [showDescription, setShowDescription] = useState(false);
  const { upcomingLive, hasCurrentLive, availableSessions } = useCurrentCloudTvSession(mode, {
    forceLiveStream,
    workshopFilter,
    enableRegistration,
    collection,
  });
  const { otherCurrentLivesConfig = {}, postLiveImage } = liveBlockConfig || {};

  const [cloudTvLive, setCloudTvLive] = useState(upcomingLive);

  const { currentLives, otherCurrentLives } = useOtherCurrentLives(cloudTvLive, {
    workshopFilter,
    otherCurrentLivesConfig,
  });

  // check if the current live playing on cloud tv is user's live or another current live
  const isUserLive = upcomingLive?._id === cloudTvLive?._id;

  useEffect(() => {
    // user is watching another current live (not his dedidacted live)
    if (!isUserLive && cloudTvLive?._id) {
      // to get the updated session with feedbacks and other data
      const otherCurrentLive = currentLives?.find((o) => o._id === cloudTvLive._id);
      if (otherCurrentLive) {
        setCloudTvLive(otherCurrentLive);
      } else {
        // other live has been cut (finished)
        setCloudTvLive(upcomingLive);
      }
    } else {
      // user is watching his live
      setCloudTvLive(upcomingLive);
    }
  }, [JSON.stringify(upcomingLive), isUserLive, currentLives]);

  const showLiveInfo = showInfoOnlyOnLive ? hasCurrentLive : true;
  const { layout, setLayout } = useContext(LayoutContext);

  if (!cloudTvLive && !availableSessions?.length) {
    // Keep current feedback open
    return (
      <>
        <FeedbackPopupBlock item={{}} />
        {postLiveImage?.image?.uri && (
          <RatioContainer ratio={16 / 9}>
            <img
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              src={Images.maxWidth(postLiveImage?.image, 1280)}
              alt="post-live"
            />
          </RatioContainer>
        )}
      </>
    );
  }

  const handleAction = (action) => {
    switch (action.type) {
      case 'info': {
        trackEvent(eventTags.ITEM_ACTION_CLICK, { item: cloudTvLive, action });
        setShowDescription(!showDescription);
        return action.type;
      }
      case 'fullscreen': {
        setLayout(layout === 'default' ? 'wide' : 'default');
        return action.type;
      }
      default:
        return noop;
    }
  };

  const handleChangeLive = (workshop) => {
    setCloudTvLive(workshop);
  };

  const now = debugNowTime || Date.now();
  const useCountdown =
    countdown?.backgroundImage &&
    countdown?.maxDate &&
    now < ensureDate(countdown.maxDate).getTime();
  const startTime = cloudTvLive?.startDate
    ? moment(cloudTvLive.startDate, moment.ISO_8601).toDate().getTime()
    : 0;
  const isNow = now > startTime - 2 * 60 * 1000;

  return (
    <>
      <FeedbackPopupBlock item={cloudTvLive || {}} />
      <div className={cx('block--cloudtv', _id)}>
        {showCurrentLive && hasCurrentLive && (
          <div className="currentLive">
            <span className="live">{t(`${translationPrefix}.live`)}</span>{' '}
            <span className="watch-live">
              {t(isNow ? `${translationPrefix}.watch-live` : `${translationPrefix}.coming-next`)}
            </span>
            <span className="title">{cloudTvLive.title}</span>
          </div>
        )}
        {useCountdown && <CdnImage src={countdown.backgroundImage} maxWidth={1920} />}
        {!hasCurrentLive && availableSessions?.length > 0 && (
          <CloudTvRegistration sessions={availableSessions} />
        )}
        {!useCountdown && cloudTvLive && (
          <WorkshopLiveBlock
            defaultActive
            // countdown={countdown}
            allowFullScreen={allowFullScreen}
            item={cloudTvLive}
            forceLiveStream={forceLiveStream}
            containerType={container?.type}
            title={replaceValues(container?.header || title, { item: cloudTvLive })}
            {...liveBlockConfig}
            videoProps={videoProps}
            hideVideo={dependsOnTypology && user?.typology?.toLowerCase?.() === 'physical'}
            showOtherCurrentLives={showOtherCurrentLives}
            otherCurrentLives={otherCurrentLives}
            onLiveChange={handleChangeLive}
          />
        )}
        <div className="block--cloudtv__content">
          {cloudTvLive && (
            <CloudTvActions actions={actions} session={cloudTvLive} onClick={handleAction} />
          )}
          {cloudTvLive && showLiveInfo && (
            <CloudTvLiveDescription
              isOpen={showDescription}
              onClose={() => setShowDescription(false)}
              session={cloudTvLive}
              {...liveDescriptionConfig}
            />
          )}
          {showOtherCurrentLives && otherCurrentLivesConfig?.position === 'bottom' && (
            <OtherCurrentLives
              workshops={otherCurrentLives}
              config={otherCurrentLivesConfig}
              onClick={handleChangeLive}
            />
          )}
        </div>
      </div>
    </>
  );
};

CloudTvBlock.defaultProps = {
  container: undefined,
  countdown: undefined,
  forceLiveStream: undefined,
  mode: 'agenda',
  options: {},
  showComments: false,
  showCurrentLive: false,
  showOtherCurrentLives: false,
  title: undefined,
  liveBlockConfig: {},
  videoProps: {},
  workshopFilter: {},
  dependsOnTypology: false,
};

CloudTvBlock.propTypes = {
  _id: PropTypes.string.isRequired,
  container: PropTypes.object,
  countdown: PropTypes.object,
  forceLiveStream: PropTypes.object,
  mode: PropTypes.string,
  showComments: PropTypes.bool,
  showCurrentLive: PropTypes.bool,
  showOtherCurrentLives: PropTypes.bool,
  title: PropTypes.string,
  options: PropTypes.shape({
    enableRegistration: PropTypes.bool,
  }),
  liveBlockConfig: PropTypes.shape({
    sidebar: sidebarProps,
  }),
  videoProps: PropTypes.shape({
    allowFullScreen: PropTypes.bool,
  }),
  workshopFilter: PropTypes.object,
  dependsOnTypology: PropTypes.bool,
};

export default connect((state) => ({
  workshops: state.workshops.workshops,
}))(CloudTvBlock);
